import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  Form,
  Button,
  Alert,
  Container,
  Row,
  Col,
  Card,
  Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import logo from '../assets/LogoNavbar.png';
import logoDark from '../assets/LogoHorizontal.png';
import { useRecordPageView } from '../hooks/useRecordPageView';
import PlanSelect from '../components/PlanSelect';
import type { Plan } from '../types/types';
import { tracking } from '../util/tracking';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { captureSupabaseError } from '../lib/sentry';

const disableRegister = true;

const freePlanId = '82a8b5f1-1959-4fc3-a37a-f6b8f16d2423';

export default function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  useRecordPageView();

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const { data } = await supabase
        .from('plans')
        .select('*')
        .order('tier', { ascending: true });

      setPlans(data ?? []);
      setLoadingPlans(false);
    };

    fetchPlans();
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    tracking.log('UI', 'register_submit', {
      page: 'register',
      planId: selectedPlanId,
    });

    if (disableRegister) {
      setError('Registration is currently disabled. Please come back later');
      return;
    }

    setError('');
    setSuccess('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const res = await supabase.functions.invoke('signup-user', {
        body: {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          plan_id: selectedPlanId,
        },
      });

      if (res.error) {
        tracking.log('ERROR', 'Signup Failed', { error: res.error.message });

        let message = 'Something went wrong';

        if (res.error instanceof FunctionsHttpError) {
          try {
            const errorBody = await res.error.context.json();
            message = errorBody.error || errorBody.message || message;
          } catch (e) {
            // fallback if body isn't JSON
            console.error('Error parsing error response:', e);
            message = res.error.message;
          }
        } else {
          message = res.error.message;
        }

        setError(message);
        return;
      }

      const { user_id, organisation_id } = res.data; // from your function response
      if (!user_id) throw new Error('No user_id returned');

      // Now trigger checkout with user email + ID
      const selectedPlan = plans.find((p) => p.id === selectedPlanId);

      if (res.data?.plan_id === freePlanId) {
        setSuccess('Registration successful! Check your email to confirm.');
        // Optionally auto-redirect to login after delay
      } else {
        if (!selectedPlan?.stripe_lookup_key) {
          setError('Plan not configured');
          return;
        }
        const { data, error } = await supabase.functions.invoke(
          'create-checkout-session',
          {
            body: {
              stripe_lookup_key: selectedPlan.stripe_lookup_key,
              email: email, // pre-fill
              user_id: user_id, // for client_reference_id
              organisation_id: organisation_id,
              flow: 'signup',
              plan_id: selectedPlan.id,
            },
          },
        );

        if (error) throw error;

        const { url } = data;

        if (url) {
          window.location.href = url;
        } else {
          setSuccess('Account created! Redirecting to payment...');
          // fallback redirect or show message
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'auth.register' });
      setError(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="bg-light min-vh-100 d-flex align-items-center">
      <Container className="py-5">
        {!selectedPlanId ? (
          <div className="text-center mb-4">
            <img
              src={logoDark}
              alt="SupplyPassport Logo"
              height="60"
              className="mb-3"
            />
            <h1 className="fw-bold">Choose Your Plan</h1>
            <p className="text-muted mb-2">
              Select the plan that best fits your needs to get started with
              SupplyPassport.
            </p>
            {loadingPlans ? (
              <div className="text-center py-4">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <PlanSelect onSelect={setSelectedPlanId} plans={plans} />
            )}
          </div>
        ) : (
          <Card className="shadow-lg rounded-3 overflow-hidden">
            <Row className="g-0">
              {/* Left column */}
              <Col
                md={6}
                className="bg-primary text-white p-4 d-flex flex-column justify-content-center align-items-center text-center"
              >
                <div>
                  <img
                    src={logo}
                    alt="SupplyPassport Logo"
                    height="80"
                    className="mb-4"
                  />
                  <h2 className="fw-bold">Join SupplyPassport</h2>
                  <p className="mt-3 mb-0">
                    Simplify your supplier compliance process and manage risks
                    with ease.
                  </p>
                </div>
              </Col>

              {/* Right column */}
              <Col
                md={6}
                className="bg-white p-4"
                style={{ minHeight: '500px' }}
              >
                <div className="mb-4">
                  <h2 className="h4 fw-bold mb-1">Create an account</h2>
                  <p className="text-muted mb-0">
                    Start managing your compliance today
                  </p>
                  {!success && (
                    <p>
                      Selected plan:{' '}
                      <strong>
                        {plans.find((plan) => plan.id === selectedPlanId)
                          ?.name || 'Unknown Plan'}
                      </strong>{' '}
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => setSelectedPlanId(null)}
                      >
                        Change
                      </Button>
                    </p>
                  )}
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success ? (
                  <Alert variant="success">{success}</Alert>
                ) : (
                  <Form onSubmit={handleRegister} className="mb-3">
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="formFirstName">
                          <Form.Label className="small">First name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="John"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3" controlId="formLastName">
                          <Form.Label className="small">Last name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Doe"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="formEmail">
                      <Form.Label className="small">Email address</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-envelope" />
                        </span>
                        <Form.Control
                          type="email"
                          placeholder="you@company.com"
                          className="border-start-0"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="formPassword">
                      <Form.Label className="small">Password</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-lock" />
                        </span>
                        <Form.Control
                          type="password"
                          placeholder="••••••••"
                          className="border-start-0"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          minLength={8}
                          required
                          disabled={loading}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group
                      className="mb-3"
                      controlId="formConfirmPassword"
                    >
                      <Form.Label className="small">
                        Confirm password
                      </Form.Label>
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0">
                          <i className="bi bi-lock" />
                        </span>
                        <Form.Control
                          type="password"
                          placeholder="••••••••"
                          className="border-start-0"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          disabled={loading}
                        />
                      </div>
                    </Form.Group>

                    <Button
                      type="submit"
                      className="w-100 btn-primary my-4"
                      disabled={loading}
                    >
                      {loading ? 'Registering...' : 'Register'}
                    </Button>
                  </Form>
                )}
              </Col>
            </Row>
          </Card>
        )}

        <p className="text-center small mt-4 mb-0">
          Already have an account?{' '}
          <Link to="/login" className="fw-semibold">
            Login here
          </Link>
        </p>

        <div className="text-center mt-3 small text-muted">
          By signing up, you agree to our{' '}
          <Link to="/terms-or-service" className="text-decoration-none">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy-policy" className="text-decoration-none">
            Privacy Policy
          </Link>
          .
        </div>
      </Container>
    </div>
  );
}
